"use client";

import { useRef } from "react";
import { Camera, ImagePlus, RotateCw, Star, X } from "lucide-react";

import { Badge, Button } from "@stocks/ui";
import { IMAGE_ALLOWED_MIME_TYPES, IMAGE_UPLOAD_MAX_FILES } from "@stocks/shared";

import type { PickedImage } from "../model/useImagePicker";

interface ImagePickerProps {
  images: PickedImage[];
  primaryId: string | null;
  disabled?: boolean;
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
  onSelectPrimary: (id: string) => void;
  /** 업로드에 실패한 사진을 다시 올린다 (없으면 재시도 버튼을 숨김) */
  onRetry?: (id: string) => void;
}

const ACCEPT = IMAGE_ALLOWED_MIME_TYPES.join(",");

/** 다중 이미지 선택/촬영, 미리보기, 개별 제거·재시도, 대표 이미지 선택. */
export const ImagePicker = ({
  images,
  primaryId,
  disabled,
  onAdd,
  onRemove,
  onSelectPrimary,
  onRetry,
}: ImagePickerProps) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onAdd(Array.from(e.target.files ?? []));
    e.target.value = ""; // 같은 파일 재선택 허용
  };

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <Button
          type='button'
          variant='outline'
          disabled={disabled}
          onClick={() => fileRef.current?.click()}
        >
          <ImagePlus className='size-4' /> 사진 선택
        </Button>
        <Button
          type='button'
          variant='outline'
          disabled={disabled}
          onClick={() => cameraRef.current?.click()}
        >
          <Camera className='size-4' /> 촬영
        </Button>
        <span className='text-xs text-muted-foreground'>
          {images.length}/{IMAGE_UPLOAD_MAX_FILES}장 · 별 아이콘으로 대표 사진 선택
        </span>
        <input
          ref={fileRef}
          type='file'
          accept={ACCEPT}
          multiple
          hidden
          onChange={handle}
          data-testid='image-file-input'
        />
        <input
          ref={cameraRef}
          type='file'
          accept={ACCEPT}
          capture='environment'
          hidden
          onChange={handle}
        />
      </div>

      {images.length > 0 && (
        <ul className='grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4'>
          {images.map((image) => {
            const isPrimary = image.id === primaryId;
            return (
              <li
                key={image.id}
                className={`relative overflow-hidden rounded-lg border ${
                  isPrimary ? "ring-2 ring-primary" : ""
                }`}
              >
                {/* 로컬 blob 미리보기 */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={image.previewUrl}
                  alt={image.file.name}
                  className='aspect-square w-full object-cover'
                />
                {isPrimary && (
                  <Badge className='absolute bottom-1 left-1'>대표</Badge>
                )}
                {image.status !== "idle" && (
                  <div className='absolute inset-x-0 top-0 bg-background/80 px-2 py-1 text-center text-xs'>
                    {image.status === "uploading" && "업로드 중…"}
                    {image.status === "done" && "업로드 완료"}
                    {image.status === "error" && (
                      <span className='text-destructive'>
                        {image.error ?? "업로드 실패"}
                      </span>
                    )}
                  </div>
                )}
                <div className='absolute right-1 bottom-1 flex gap-1'>
                  {image.status === "error" && onRetry && (
                    <Button
                      type='button'
                      size='icon-sm'
                      variant='secondary'
                      aria-label='재시도'
                      onClick={() => onRetry(image.id)}
                    >
                      <RotateCw className='size-3.5' />
                    </Button>
                  )}
                  <Button
                    type='button'
                    size='icon-sm'
                    variant={isPrimary ? "default" : "secondary"}
                    aria-label='대표 사진으로 선택'
                    disabled={disabled}
                    onClick={() => onSelectPrimary(image.id)}
                  >
                    <Star className='size-3.5' />
                  </Button>
                  {image.status !== "done" && (
                    <Button
                      type='button'
                      size='icon-sm'
                      variant='secondary'
                      aria-label='사진 제거'
                      disabled={disabled}
                      onClick={() => onRemove(image.id)}
                    >
                      <X className='size-3.5' />
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
